<template>
  <Card class="deadlines-widget">
    <template #default>
      <div class="deadlines-widget__title row middle g-1">
        <div class="card__title">{{ $t('deadlines') }}</div>

        <DropdownComponent placement="bottom-start">
          <ButtonComponent :label="triggerLabel" />

          <template #dropdown>
            <ButtonComponent
              v-for="preset in presets"
              :key="preset.value"
              :icon="preset.value === selected ? ['fal', 'check'] : null"
              @click="setFilter(preset.value)"
            >
              <template #label>
                <span class="row middle g-1">
                  <FontAwesomeIcon :icon="preset.icon" />
                  <span>{{ preset.label }}</span>
                </span>
              </template>
            </ButtonComponent>
          </template>
        </DropdownComponent>
      </div>

      <Loader :value="promise" :delay="0" class="deadlines-widget__content">
        <div v-if="results.length" class="deadlines-widget__items">
          <component :is="item.typename" v-for="(item, index) in results" :key="index" :value="item" />
        </div>
        <EmptyState v-else :title="activePreset.emptyTitle" :icon="['fal', 'calendar']" class="grow-1 mb-2" />
      </Loader>
    </template>
  </Card>
</template>

<script>
import moment from 'moment'
import API from '@/api/Deadline'
import Card from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import { DropdownComponent } from 'vue-elder-dropdown'
import Types from './Types'

const DefaultPreset = 'upcoming'

export default {
  props: {
    reference: {
      type: Object,
      required: true,
    },
  },
  watch: {
    'reference.ref': {
      handler: 'reset',
      immediate: true,
    },
  },
  data() {
    return {
      selected: DefaultPreset,
      results: [],
      promise: null,
    }
  },
  computed: {
    presets() {
      return [
        {
          value: 'upcoming',
          label: this.$t('deadlineUpcoming'),
          icon: ['fal', 'inbox-in'],
          emptyTitle: this.$t('noDeadlines'),
          filter: {
            range: {
              from: moment().startOf('day').toDate(),
              to: moment().endOf('day').add(1, 'week').toDate(),
            },
          },
          sort: { date: 1 },
        },
        {
          value: 'expired',
          label: this.$t('deadlineExpired'),
          icon: ['fal', 'inbox-out'],
          emptyTitle: this.$t('noExpiredDeadlines'),
          filter: {
            range: { to: moment().toDate() },
            types: ['!Openhouse'],
          },
          sort: { date: -1 },
        },
      ]
    },
    activePreset() {
      return this.presets.find(p => p.value === this.selected)
    },
    count() {
      return this.results.length
    },
    triggerLabel() {
      return `${this.count} ${this.activePreset.label}`
    },
  },
  methods: {
    fetch() {
      if (!this.reference.ref) return

      this.promise = API.get({
        reference: this.reference,
        filter: this.activePreset.filter,
        sort: this.activePreset.sort,
      }).then(({ deadlines }) => (this.results = deadlines))

      return this.promise
    },
    setFilter(value) {
      this.selected = value
      return this.fetch()
    },
    reset() {
      this.selected = DefaultPreset
      this.results = []
      return this.fetch()
    },
  },
  created() {
    this.$bus.$on('task:create', this.fetch)
    this.$bus.$on('task:update', this.fetch)
    this.$bus.$on('task:remove', this.fetch)
  },
  beforeDestroy() {
    this.$bus.$off('task:create', this.fetch)
    this.$bus.$off('task:update', this.fetch)
    this.$bus.$off('task:remove', this.fetch)
  },
  components: {
    Card,
    EmptyState,
    DropdownComponent,
    ...Types,
  },
}
</script>

<style lang="scss">
.deadlines-widget {
  overflow: hidden;

  .card__content {
    display: flex;
    flex-direction: column;
    padding: 0;
    flex-grow: 1;
    overflow-y: auto;

    @include scrollShadow();
  }

  .card__title {
    margin-bottom: 0;
  }

  &__title {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: var(--card-spacing);
    background: linear-gradient(to bottom, white 55%, rgba(255, 255, 255, 0));
  }

  &__title .elder-dropdown__trigger .elder-button {
    --vue-elder-button-padding-y: 0.25em;
    --vue-elder-button-padding-x: 0.6em;
    --vue-elder-default: hsl(var(--grey-h), var(--grey-s), 95%);
    --vue-elder-default-contrast: hsl(var(--grey-h), var(--grey-s), 20%);
    --vue-elder-default-border: transparent;

    font-size: var(--font-size-small);
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__items {
    padding: 0 var(--card-spacing) var(--card-spacing) var(--card-spacing);
    margin-top: calc(var(--card-spacing) * -0.5);
  }
}
</style>
